import React, { useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import {
  getVanhoaDistribution,
  generateVanhoaDistribution,
  getVanhoaDistributionDraft,
  setVanhoaDistributionDraft,
  applyVanhoaDistributionDraft,
  setVanhoaDistributionLock,
  listMySchools,
} from "./happyschoolApi";
import { getActiveSchoolId } from "./activeSchool";

const DAYS = ["Thứ 2", "Thứ 3", "Thứ 4", "Thứ 5", "Thứ 6", "Thứ 7"];
const CELL = {
  nghe: { label: "Nghề", bg: "#fde68a", fg: "#92400e" },
  vanhoa: { label: "Văn hoá", bg: "#bfdbfe", fg: "#1e40af" },
  nghi: { label: "Nghỉ", bg: "#f3f4f6", fg: "#9ca3af" },
  thiks: { label: "Thi KS", bg: "#fecaca", fg: "#991b1b" },
};

const Cell = ({ v }) => {
  const c = CELL[v] || CELL.nghi;
  return <div style={{ background: c.bg, color: c.fg, borderRadius: 4, padding: "3px 4px", fontSize: 11, fontWeight: 600, textAlign: "center" }}>{c.label}</div>;
};

// Ô sửa được (Bản đang chờ duyệt) — chọn Nghề/Văn hoá/Nghỉ ngay trên bảng.
const CellSelect = ({ v, onChange }) => {
  const c = CELL[v] || CELL.nghi;
  return (
    <select
      value={v}
      onChange={(e) => onChange(e.target.value)}
      style={{ background: c.bg, color: c.fg, borderRadius: 4, padding: "3px 2px", fontSize: 11, fontWeight: 600, textAlign: "center", border: "1px solid rgba(0,0,0,.15)", width: "100%" }}
    >
      {Object.entries(CELL).map(([k, cc]) => (<option key={k} value={k}>{cc.label}</option>))}
    </select>
  );
};

// Webview nhúng trong Zalo/Messenger/... chặn tải file (Blob download) trên iOS.
function isBlockedInAppBrowser() {
  const ua = navigator.userAgent || "";
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isInApp = /Zalo|FBAN|FBAV|Instagram|Line\/|MicroMessenger/i.test(ua);
  return isIOS && isInApp;
}
function downloadArrayBuffer(buf, fileName) {
  if (isBlockedInAppBrowser()) {
    window.alert(
      "Trình duyệt trong ứng dụng (Zalo/Messenger/...) trên iPhone không cho phép tải file.\n\n" +
      "Hãy bấm nút menu (⋮ hoặc ...) ở góc trên, chọn \"Mở bằng trình duyệt\" (Safari), rồi bấm Export lại."
    );
    return;
  }
  const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// weekId: tuần TKB đang chọn. "Bản chính" hiện cho GV xem; "Bản đang chờ duyệt" dùng để
// nhập Excel/sửa thử — chỉ Owner mới "Duyệt" được để ghi vào bản chính (bản
// chính cũ tự động lưu vào lịch sử trước khi bị ghi đè).
const HappySchoolVanhoaDistribution = ({ weekId, readOnly }) => {
  const schoolId = getActiveSchoolId();
  const [data, setData] = useState(null); // bản chính
  const [draftData, setDraftData] = useState(null); // bản đang chờ duyệt (chỉ xem)
  const [source, setSource] = useState("chinh"); // "chinh" | "nhap"
  const [isOwner, setIsOwner] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false); // owner hoặc admin
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState("");
  const [view, setView] = useState("byClass"); // "byClass" | "byKhoi"
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState("");
  const [savingDraft, setSavingDraft] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editBuffer, setEditBuffer] = useState(null); // rows đang sửa tạm (chưa lưu)
  const [lockToggling, setLockToggling] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    if (!schoolId || !weekId) return;
    setLoading(true);
    setError("");
    Promise.all([
      getVanhoaDistribution(schoolId, weekId),
      readOnly ? Promise.resolve(null) : getVanhoaDistributionDraft(schoolId, weekId),
    ])
      .then(([d, dr]) => { setData(d); setDraftData(dr); })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, [schoolId, weekId]); // eslint-disable-line

  // Chỉ Owner mới được Duyệt bản đang chờ duyệt vào bản chính; Owner/Admin mới được
  // "Tạo lại từ TKB nghề" (ghi thẳng bản chính) — nhóm Giáo vụ chỉ Nhập Excel.
  useEffect(() => {
    if (!schoolId || readOnly) { setIsOwner(false); setIsAdmin(false); return; }
    listMySchools()
      .then((list) => {
        const s = (list || []).find((x) => x.id === schoolId);
        setIsOwner(!!s && s.role === "owner");
        setIsAdmin(!!s && (s.role === "owner" || s.role === "admin"));
      })
      .catch(() => { setIsOwner(false); setIsAdmin(false); });
  }, [schoolId, readOnly]);

  const regen = async () => {
    setBusy(true);
    setError("");
    try {
      const d = await generateVanhoaDistribution(schoolId, weekId);
      setData(d);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const applyDraft = async () => {
    if (!window.confirm("Duyệt bản đang chờ duyệt vào bản chính? Bản chính hiện tại sẽ được lưu vào lịch sử trước khi bị ghi đè.")) return;
    setApplying(true);
    setError("");
    try {
      const record = await applyVanhoaDistributionDraft(schoolId, weekId);
      setData(record);
      setSource("chinh");
      setImportMsg("Đã duyệt bản đang chờ duyệt vào bản chính.");
    } catch (err) {
      setError(err.message);
    } finally {
      setApplying(false);
    }
  };

  const activeData = source === "nhap" ? draftData : data;
  // Khoá riêng cho TRANG ADMIN (khác hẳn readOnly — readOnly là quyền theo vai
  // trò/self-service; locked là do Owner/Admin tự khoá tay để tránh sửa nhầm
  // bản chính, xem cả 2 loại). Khoá thì admin cũng chỉ xem, không sửa/tạo lại/
  // nhập Excel/duyệt được — chỉ mở khoá mới làm lại được.
  const locked = !!(data && data.locked);
  // "Bản đang chờ duyệt" CHỈ ĐỂ XEM (review trước khi Owner duyệt) — sửa luôn
  // thực hiện ngay trên "Bản chính" (bấm "✏️ Sửa"), lưu vào bản chờ duyệt.
  const rows = (source === "chinh" && editMode ? editBuffer : (activeData && activeData.rows)) || [];
  const canEditHere = !readOnly && !locked && source === "chinh" && !editMode;
  const editable = !readOnly && !locked && source === "chinh" && editMode;

  const toggleLock = async () => {
    if (!data) return;
    const next = !locked;
    setLockToggling(true);
    setError("");
    try {
      await setVanhoaDistributionLock(schoolId, weekId, next);
      setData((d) => (d ? { ...d, locked: next } : d));
    } catch (err) {
      setError(err.message);
    } finally {
      setLockToggling(false);
    }
  };

  const startEdit = () => {
    setEditBuffer(JSON.parse(JSON.stringify((data && data.rows) || [])));
    setEditMode(true);
  };

  const setCellValue = (className, dayIdx, buoi, value) => {
    setEditBuffer((prev) => (prev || []).map((r) => {
      if (r.className !== className) return r;
      const cells = r.cells.map((c, i) => (i === dayIdx ? { ...c, [buoi]: value } : c));
      return { ...r, cells };
    }));
  };

  const saveDraft = async () => {
    setSavingDraft(true);
    setError("");
    try {
      const record = await setVanhoaDistributionDraft(schoolId, weekId, {
        rows: editBuffer || [],
        notes: (data && data.notes) || undefined,
        khoiSummary: (data && data.khoiSummary) || undefined,
        periodTable: (data && data.periodTable) || undefined,
      });
      setDraftData(record);
      setImportMsg('Đã lưu vào bản đang chờ duyệt — chờ Owner bấm "Duyệt vào bản chính".');
      setEditMode(false);
      setEditBuffer(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingDraft(false);
    }
  };

  // Huỷ sửa — bỏ hết thay đổi chưa lưu, quay lại xem đúng bản chính hiện tại.
  const cancelEdit = () => {
    setEditMode(false);
    setEditBuffer(null);
    setError("");
  };

  const exportExcel = async () => {
    if (rows.length === 0) return;
    const lbl = (v) => (CELL[v] || CELL.nghi).label;
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Phân phối nghề-văn hoá");
    ws.mergeCells(1, 1, 2, 1);
    ws.getCell(1, 1).value = "Lớp";
    DAYS.forEach((d, i) => {
      const col = 2 + i * 2;
      ws.mergeCells(1, col, 1, col + 1);
      ws.getCell(1, col).value = d;
      ws.getCell(2, col).value = "Sáng";
      ws.getCell(2, col + 1).value = "Chiều";
    });
    rows.forEach((r, idx) => {
      const rowNum = 3 + idx;
      ws.getCell(rowNum, 1).value = r.className;
      r.cells.forEach((c, i) => {
        const col = 2 + i * 2;
        ws.getCell(rowNum, col).value = lbl(c.sang);
        ws.getCell(rowNum, col + 1).value = lbl(c.chieu);
      });
    });
    ws.getColumn(1).width = 10;
    for (let i = 0; i < DAYS.length * 2; i++) ws.getColumn(2 + i).width = 9;
    // Combobox chọn Nghề/Văn hoá/Nghỉ cho toàn bộ ô dữ liệu.
    const lastRow = 2 + rows.length;
    const lastCol = 1 + DAYS.length * 2;
    for (let row = 3; row <= lastRow; row++) {
      for (let col = 2; col <= lastCol; col++) {
        ws.getCell(row, col).dataValidation = {
          type: "list",
          allowBlank: false,
          formulae: ['"Nghề,Văn hoá,Nghỉ"'],
        };
      }
    }
    const buf = await wb.xlsx.writeBuffer();
    downloadArrayBuffer(buf, `lich_phan_phoi_nghe_van_hoa_${source === "nhap" ? "nhap" : "chinh"}.xlsx`);
  };

  // Nhập từ Excel — LUÔN ghi vào bản đang chờ duyệt (không đụng bản chính). Đúng bố cục
  // đã Xuất: cột 0 = Lớp, mỗi ngày 2 cột (Sáng/Chiều) ghi "Nghề"/"Văn hoá"/"Nghỉ".
  const importExcel = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    setImportMsg("");
    setError("");
    setImporting(true);
    try {
      const labelToKey = {};
      Object.entries(CELL).forEach(([k, c]) => { labelToKey[c.label.toLowerCase().trim()] = k; });
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
      if (aoa.length < 3) throw new Error("File rỗng hoặc sai định dạng.");

      const newRows = [];
      for (let r = 2; r < aoa.length; r++) {
        const row = aoa[r] || [];
        const className = String(row[0] || "").trim();
        if (!className) continue;
        const cells = DAYS.map((_, i) => {
          const sangLabel = String(row[1 + i * 2] || "").toLowerCase().trim();
          const chieuLabel = String(row[2 + i * 2] || "").toLowerCase().trim();
          return {
            sang: labelToKey[sangLabel] || "nghi",
            chieu: labelToKey[chieuLabel] || "nghi",
          };
        });
        newRows.push({ className, cells });
      }
      if (newRows.length === 0) throw new Error("Không đọc được lớp nào trong file.");

      const record = await setVanhoaDistributionDraft(schoolId, weekId, {
        rows: newRows,
        notes: (data && data.notes) || undefined,
        khoiSummary: (data && data.khoiSummary) || undefined,
        periodTable: (data && data.periodTable) || undefined,
      });
      setDraftData(record);
      setSource("nhap");
      setImportMsg(`Đã nhập ${newRows.length} lớp vào BẢN NHÁP. Kiểm tra rồi bấm "✅ Duyệt vào bản chính" để áp dụng.`);
    } catch (err) {
      setError("Lỗi nhập Excel: " + err.message);
    } finally {
      setImporting(false);
    }
  };

  if (!weekId) return <p style={{ color: "#6b7280" }}>Chưa chọn tuần TKB.</p>;
  if (loading) return <p>Đang tải...</p>;

  // Tổng số lớp học Văn hoá / Nghề theo từng buổi (sáng/chiều mỗi ngày).
  const countByBuoi = (kind) =>
    DAYS.map((_, i) => ({
      sang: rows.reduce((s, r) => s + (r.cells[i]?.sang === kind ? 1 : 0), 0),
      chieu: rows.reduce((s, r) => s + (r.cells[i]?.chieu === kind ? 1 : 0), 0),
    }));
  const vanhoaTotals = countByBuoi("vanhoa");
  const ngheTotals = countByBuoi("nghe");

  return (
    <div>
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
        {!readOnly && (
          <div style={{ display: "flex", gap: 6 }}>
            <button type="button" onClick={() => setSource("chinh")} style={source === "chinh" ? tabActive : tabInactive}>📄 Bản chính</button>
            <button type="button" onClick={() => setSource("nhap")} style={source === "nhap" ? tabActive : tabInactive}>📝 Bản đang chờ duyệt (chỉ xem)</button>
          </div>
        )}
        <div style={{ display: "flex", gap: 6 }}>
          <button type="button" onClick={() => setView("byClass")} style={view === "byClass" ? tabActive : tabInactive}>Theo lớp</button>
          <button type="button" onClick={() => setView("byKhoi")} style={view === "byKhoi" ? tabActive : tabInactive}>Theo khối</button>
        </div>
        {!readOnly && !locked && isAdmin && source === "chinh" && (
          <button type="button" onClick={regen} disabled={busy} style={genBtn}>
            {busy ? "Đang tạo..." : "🔄 Tạo lại từ TKB nghề"}
          </button>
        )}
        <button type="button" onClick={exportExcel} disabled={rows.length === 0} style={exportBtn}>
          ⬇ Xuất Excel
        </button>
        {!readOnly && !locked && (
          <>
            <button type="button" onClick={() => fileRef.current && fileRef.current.click()} disabled={importing} style={exportBtn}>
              {importing ? "Đang nhập..." : "⬆ Nhập Excel"}
            </button>
            <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={importExcel} style={{ display: "none" }} />
          </>
        )}
        {!readOnly && isAdmin && data && (
          <button type="button" onClick={toggleLock} disabled={lockToggling}
            title={locked ? "Mở khoá để sửa/tạo lại/nhập Excel/duyệt được lại" : "Khoá lại: chỉ xem, không sửa/tạo lại/nhập Excel/duyệt được nữa"}
            style={locked ? openBtnStyle : closeBtnStyle}>
            {lockToggling ? "..." : locked ? "🔓 Mở khoá" : "🔒 Khoá lại"}
          </button>
        )}
        {canEditHere && (
          <button type="button" onClick={startEdit} disabled={rows.length === 0} style={genBtn}>
            ✏️ Sửa
          </button>
        )}
        {editable && (
          <>
            <button type="button" onClick={saveDraft} disabled={savingDraft} style={genBtn}>
              {savingDraft ? "Đang lưu..." : "💾 Lưu"}
            </button>
            <button type="button" onClick={cancelEdit} disabled={savingDraft} style={exportBtn}>
              Huỷ sửa
            </button>
          </>
        )}
        {!readOnly && !locked && source === "nhap" && isOwner && (
          <button type="button" onClick={applyDraft} disabled={applying || rows.length === 0} style={applyBtn}>
            {applying ? "Đang duyệt..." : "✅ Duyệt vào bản chính"}
          </button>
        )}
        {!readOnly && !locked && source === "nhap" && !isOwner && (
          <span style={{ fontSize: 12, color: "#b45309" }}>Chỉ Owner mới được duyệt bản đang chờ duyệt vào bản chính.</span>
        )}
        {activeData && activeData.generatedAt && (
          <span style={{ fontSize: 12, color: "#6b7280" }}>Cập nhật lúc {new Date(activeData.generatedAt).toLocaleString("vi-VN")}</span>
        )}
        <div style={{ display: "flex", gap: 10, marginLeft: "auto", flexWrap: "wrap" }}>
          {Object.entries(CELL).map(([k, c]) => (
            <span key={k} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12 }}>
              <span style={{ width: 12, height: 12, borderRadius: 3, background: c.bg, display: "inline-block" }} /> {c.label}
            </span>
          ))}
        </div>
      </div>
      {!readOnly && locked && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "8px 14px", marginBottom: 10, fontSize: 13, color: "#991b1b", fontWeight: 600 }}>
          🔒 Đã khoá bản chính tuần này — chỉ xem, không sửa/tạo lại/nhập Excel/duyệt được. Bấm "🔓 Mở khoá" để sửa lại.
        </div>
      )}
      {activeData && activeData.notes && activeData.notes.length > 0 && (
        <div style={{ background: "#fefce8", border: "1px solid #fde68a", borderRadius: 8, padding: "8px 14px", marginBottom: 10 }}>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: "#78350f", lineHeight: 1.6 }}>
            {activeData.notes.map((n, i) => (<li key={i}>{n}</li>))}
          </ul>
        </div>
      )}
      {error && <p style={{ color: "#dc2626" }}>{error}</p>}
      {importMsg && <p style={{ color: "#059669", fontSize: 13 }}>{importMsg}</p>}
      {activeData && activeData.orphanClasses && activeData.orphanClasses.length > 0 && (
        <p style={{ color: "#b45309", fontSize: 12 }}>
          ⚠ Có trong TKB nghề nhưng chưa có trong danh sách lớp: {activeData.orphanClasses.join(", ")} (chưa cập nhật vào lịch xếp TKB).
        </p>
      )}
      {rows.length === 0 ? (
        <p style={{ color: "#6b7280" }}>
          {source === "nhap" ? 'Bản đang chờ duyệt đang trống — bấm "⬆ Nhập Excel" để nạp dữ liệu.' : 'Chưa có dữ liệu — bấm "🔄 Tạo lại từ TKB nghề".'}
        </p>
      ) : view === "byKhoi" ? (
        <KhoiSummaryView khoiSummary={(activeData && activeData.khoiSummary) || []} periodTable={(activeData && activeData.periodTable) || null} />
      ) : (
        <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 8 }}>
          <table style={{ borderCollapse: "collapse", fontSize: 12, width: "100%" }}>
            <thead>
              <tr>
                <th rowSpan={2} style={{ ...thStyle, position: "sticky", left: 0, zIndex: 2 }}>Lớp</th>
                {DAYS.map((d) => (
                  <th key={d} colSpan={2} style={thStyle}>{d}</th>
                ))}
              </tr>
              <tr>
                {DAYS.map((d) => [
                  <th key={d + "s"} style={thSub}>Sáng</th>,
                  <th key={d + "c"} style={thSub}>Chiều</th>,
                ])}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.className}>
                  <td style={{ ...tdName, position: "sticky", left: 0, background: "#fff", zIndex: 1 }}>
                    {r.className}{r.orphan ? " ⚠" : ""}
                  </td>
                  {r.cells.map((c, i) => [
                    <td key={i + "s"} style={tdCell}>
                      {editable ? <CellSelect v={c.sang} onChange={(val) => setCellValue(r.className, i, "sang", val)} /> : <Cell v={c.sang} />}
                    </td>,
                    <td key={i + "c"} style={tdCell}>
                      {editable ? <CellSelect v={c.chieu} onChange={(val) => setCellValue(r.className, i, "chieu", val)} /> : <Cell v={c.chieu} />}
                    </td>,
                  ])}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td style={{ ...tdTotalName, position: "sticky", left: 0, background: "#eff6ff", zIndex: 1 }}>
                  Tổng Văn hoá
                </td>
                {vanhoaTotals.map((t, i) => [
                  <td key={i + "s"} style={tdTotal}>{t.sang}</td>,
                  <td key={i + "c"} style={tdTotal}>{t.chieu}</td>,
                ])}
              </tr>
              <tr>
                <td style={{ ...tdTotalName, position: "sticky", left: 0, background: "#fffbeb", zIndex: 1 }}>
                  Tổng Nghề
                </td>
                {ngheTotals.map((t, i) => [
                  <td key={i + "s"} style={{ ...tdTotal, background: "#fffbeb" }}>{t.sang}</td>,
                  <td key={i + "c"} style={{ ...tdTotal, background: "#fffbeb" }}>{t.chieu}</td>,
                ])}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

// Xem "Theo khối" — 1 dòng mỗi khối (+ Lớp tự túc), khớp đúng bố cục bảng
// gốc trong ảnh "DỰ KIẾN LỊCH HỌC": Thứ2-7 (x/Nghỉ) + cột Ghi chú, cộng bảng
// nhỏ "Thời lượng học". Dữ liệu lấy từ data.khoiSummary / data.periodTable.
const KhoiSummaryView = ({ khoiSummary, periodTable }) => {
  if (khoiSummary.length === 0) {
    return <p style={{ color: "#6b7280" }}>Chưa có dữ liệu tổng quan theo khối cho tuần này.</p>;
  }
  return (
    <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-start" }}>
      <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 8, flex: "1 1 560px" }}>
        <table style={{ borderCollapse: "collapse", fontSize: 12, width: "100%" }}>
          <thead>
            <tr>
              <th style={thStyle}>Khối</th>
              {DAYS.map((d) => (<th key={d} style={thStyle}>{d}</th>))}
              <th style={{ ...thStyle, textAlign: "left" }}>Ghi chú</th>
            </tr>
          </thead>
          <tbody>
            {khoiSummary.map((row) => (
              <tr key={row.khoi}>
                <td style={{ ...tdName, fontWeight: 700 }}>{row.khoi}</td>
                {row.days.map((v, i) => (
                  <td key={i} style={{ ...tdCell, background: v === "Nghỉ" ? "#f3f4f6" : v === "x" ? "#fef9c3" : "#fff", textAlign: "center", fontWeight: 600, color: v === "Nghỉ" ? "#9ca3af" : "#78350f" }}>
                    {v || "—"}
                  </td>
                ))}
                <td style={{ ...tdCell, textAlign: "left", fontSize: 11 }}>{row.note || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {periodTable && (
        <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden", flex: "0 0 auto" }}>
          <table style={{ borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr>
                <th style={thStyle}></th>
                <th style={thStyle}>Nghề</th>
                <th style={thStyle}>VH chính khoá</th>
                <th style={thStyle}>VH TC</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ ...tdName, fontWeight: 600 }}>Thời lượng học</td>
                <td style={tdCell}>{periodTable.nghe}</td>
                <td style={tdCell}>{periodTable.vhChinhKhoa}</td>
                <td style={tdCell}>{periodTable.vhTangCuong}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const thStyle = { border: "1px solid #e5e7eb", padding: "5px 8px", background: "#f9fafb", whiteSpace: "nowrap", textAlign: "center" };
const thSub = { ...thStyle, fontWeight: 500, color: "#6b7280", fontSize: 11 };
const tdName = { border: "1px solid #e5e7eb", padding: "4px 8px", fontWeight: 600, whiteSpace: "nowrap" };
const tdCell = { border: "1px solid #e5e7eb", padding: 2 };
const tdTotalName = { border: "1px solid #e5e7eb", padding: "4px 8px", fontWeight: 700, whiteSpace: "nowrap" };
const tdTotal = { border: "1px solid #e5e7eb", padding: 4, textAlign: "center", fontWeight: 700, background: "#eff6ff" };
const genBtn = { padding: "7px 14px", background: "#0ea5a3", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13 };
const applyBtn = { padding: "7px 14px", background: "#dc2626", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 700, fontSize: 13 };
const tabInactive = { padding: "6px 12px", background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 999, cursor: "pointer", fontWeight: 600, fontSize: 12 };
const tabActive = { ...tabInactive, background: "#0ea5a3", color: "#fff", border: "1px solid #0ea5a3" };
const exportBtn = { padding: "7px 14px", background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13 };
const closeBtnStyle = { padding: "7px 14px", background: "#dc2626", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 700, fontSize: 13 };
const openBtnStyle = { padding: "7px 14px", background: "#059669", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 700, fontSize: 13 };

export default HappySchoolVanhoaDistribution;
